import { useState, useCallback } from "react";
import { httpsCallable } from "firebase/functions";
import { functionsAsia } from "@/lib/firebase";

export type CopilotMessage = {
  role: "user" | "assistant";
  content: string;
};

type AskRequest = {
  message: string;
  history: { role: "user" | "model"; parts: { text: string }[] }[];
  source?: "voice" | "text";
};

type AskResponse = {
  reply: string;
  usedToolCalls: string[];
};

const askFn = httpsCallable<AskRequest, AskResponse>(functionsAsia, "askDailyHisaabAI");
const MAX_HISTORY = 6;

export function useCopilot() {
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = useCallback(async (text: string, source?: "voice" | "text") => {
    const trimmed = text.trim();
    if (!trimmed || pending) return;
    if (trimmed.length > 500) {
      setError("Message too long (max 500 characters).");
      return;
    }

    setError(null);
    setPending(true);

    const userMsg: CopilotMessage = { role: "user", content: trimmed };
    const priorHistory = messages.slice(-MAX_HISTORY);
    setMessages((m) => [...m, userMsg]);

    try {
      const geminiHistory = priorHistory.map((m) => ({
        role: m.role === "assistant" ? ("model" as const) : ("user" as const),
        parts: [{ text: m.content }],
      }));
      const res = await askFn({ message: trimmed, history: geminiHistory, source });
      const reply = res.data.reply || "I didn't get that. Please try again.";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (err: any) {
      const code = err?.code || "";
      let msg: string;
      if (code === "functions/resource-exhausted") {
        msg = err?.message || "You've hit today's limit. Try again tomorrow.";
      } else if (code === "functions/invalid-argument") {
        msg = err?.message || "Invalid request.";
      } else if (code === "functions/unauthenticated") {
        msg = "Please sign in to use Saathi.";
      } else {
        msg = "Saathi is offline, try again later.";
      }
      setError(msg);
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${msg}` }]);
    } finally {
      setPending(false);
    }
  }, [messages, pending]);

  const clear = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return { messages, pending, error, send, clear };
}
